// src/services/api.js
import axios from 'axios';

// Determine API base URL
const getApiBaseUrl = () => {
  // Check environment variable first (should be https://api.intellinoteai.com/api)
  if (process.env.REACT_APP_API_URL) {
    console.log('Using REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // Server-side rendering or no window
  if (typeof window === 'undefined') {
    return 'http://localhost:5000/api';
  }
  
  const hostname = window.location.hostname;
  console.log('Detecting API URL for hostname:', hostname);
  
  // Production domain
  if (hostname === 'www.intellinoteai.com' || hostname === 'intellinoteai.com') {
    console.log('Using production API: https://api.intellinoteai.com/api');
    return 'https://api.intellinoteai.com/api';
  }
  
  // Vercel preview deployments
  if (hostname.includes('.vercel.app')) {
    const url = window.location.origin + '/api';
    console.log('Using Vercel preview API:', url);
    return url;
  }
  
  // Local development
  console.log('Using localhost API');
  return 'http://localhost:5000/api';
};

const baseURL = getApiBaseUrl();
console.log('API BaseURL configured as:', baseURL);

// Create axios instance with default config
const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
  withCredentials: true // allow cookies to be sent for auth
});

// Request interceptor - add auth token to requests
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    // If token exists, add to headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => {
    // Return successful response data
    return response;
  },
  (error) => {
    // Create user-friendly error message
    let userMessage = 'Something went wrong. Please try again.';
    
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url
      });
      
      // Use server message if available
      if (error.response.data?.message) {
        userMessage = error.response.data.message;
      }
      
      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized
          if (error.config && error.config.headers && error.config.headers.Authorization) {
            userMessage = 'Your session has expired. Please sign in again.';
            localStorage.removeItem('token');
            window.location.href = '/login';
          } else if (!error.response.data?.message) {
            userMessage = 'Please sign in to continue.';
          }
          break;
        case 403:
          userMessage = error.response.data?.message || 'You don\'t have permission to do that.';
          break;
        case 404:
          userMessage = error.response.data?.message || 'The item you\'re looking for doesn\'t exist.';
          break;
        case 429:
          userMessage = 'Too many requests. Please slow down and try again in a moment.';
          break;
        case 500:
        case 502:
        case 503:
          userMessage = 'Our server is having trouble. Please try again in a moment.';
          break;
        default:
          // Keep the server message or use default
          break;
      }
    } else if (error.request) {
      // Request made but no response received
      userMessage = 'Unable to connect. Please check your internet connection.';
      console.error('No response received:', error.request);
    } else {
      // Something else happened
      userMessage = 'An unexpected error occurred. Please try again.';
      console.error('Error setting up request:', error.message);
    }
    
    // Attach user-friendly message to error object
    error.userMessage = userMessage;
    
    return Promise.reject(error);
  }
);

// Optional: Add methods for specific content types
export const uploadFile = async (url, file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
};

export default api;